import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TabErrorBanner from "@/components/v2/molecules/admin/shared/TabErrorBanner";
import GuestsEmptyBanner from "@/components/v2/molecules/admin/tabs/AdminGuestsTab/GuestsEmptyBanner";
import GuestsFilteredBanner from "@/components/v2/molecules/admin/tabs/AdminGuestsTab/GuestsFilteredBanner";
import GuestsTabSkeleton from "@/components/v2/molecules/admin/tabs/AdminGuestsTab/GuestsTabSkeleton";
import GuestFormModal from "@/components/v2/organisms/admin/tabs/AdminGuestsTab/GuestFormModal";
import useGuestModal from "@/hooks/admin/AdminGuestsTab/useGuestsModal";
import useRolePermissions from "@/hooks/admin/shared/useRolePermissions";
import { useGuests } from "@/hooks/useGuests";
import { Plus, Search } from "lucide-react";
import { useState } from "react";
import GuestsTable from "./GuestsTable";
import AdminPageHeader from '@/components/v2/atoms/admin/layout/AdminPageHeader';

const AdminGuestsTab = () => {
  const { canEdit, canDelete, canCreate, role, userId } = useRolePermissions();

  const {
    data: guests = [],
    isLoading: isLoadingGuests,
    error: errorGuests,
    refetch: refetchGuests,
  } = useGuests({role, userId});

  const [searchTerm, setSearchTerm] = useState("");

  const {
    isModalOpen,
    guestToEdit,
    openCreateModal,
    openEditModal,
    closeModal,
    handleDeleteGuest,
  } = useGuestModal();

  const filteredGuests = guests.filter((guest) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const nameMatch = guest.name.toLowerCase().includes(searchLower);
    const emailMatch =
      guest.email?.toLowerCase().includes(searchLower) || false;

    const originMatch =
      guest.origins?.name.toLowerCase().includes(searchLower) || false;

    return nameMatch || emailMatch || originMatch;
  });

  if (isLoadingGuests) {
    return <GuestsTabSkeleton />;
  }

  if (errorGuests) {
    return (
      <TabErrorBanner
        title="Error al cargar huéspedes"
        message="No se pudieron cargar los huéspedes. Por favor, intenta nuevamente."
        onRetry={() => refetchGuests()}
      />
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-3">
          <AdminPageHeader title="Huéspedes">
            {canCreate && guests.length !== 0 && (
              <Button
                onClick={openCreateModal}
                className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
              >
                <Plus size={16} />
                Agregar Huésped
              </Button>
            )}
          </AdminPageHeader>

          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />

            <Input
              placeholder="Buscar por nombre, correo u origen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-[300px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 h-10"
            />
          </div>
        </div>

        {guests.length === 0 ? (
          <GuestsEmptyBanner onCreate={openCreateModal} />
        ) : filteredGuests.length === 0 ? (
          <GuestsFilteredBanner
            searchTerm={searchTerm}
            onClear={() => setSearchTerm("")}
          />
        ) : (
          <GuestsTable
            guests={filteredGuests}
            onEdit={openEditModal}
            onDelete={handleDeleteGuest}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        )}
      </div>

      <GuestFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        guest={guestToEdit}
      />
    </>
  );
};

export default AdminGuestsTab;
